/**
 * Option 1 gradient system (LOCKED).
 *
 * Every tool icon is a gradient that runs the tool's own start hue →
 * its suite's master hue. This makes a glyph, on its own, signal which
 * suite it belongs to (it resolves toward that suite's color), while
 * each tool stays chromatically distinct. NerdStack is treated as a
 * peer "suite" whose master is the family violet.
 *
 * Light start hues are CLAMPED (kept deep enough) so the light end of
 * the gradient never washes out against the dark #0A0B14 background —
 * there is no fill plate behind the glyph, so the stroke color does all
 * the work and must stay legible at both ends.
 *
 * Suite masters (locked, = each suite's in-app accent / icon start hue):
 *   Chem  #F8B84E   Math #2DD4BF   Phys #22B8F0
 *   Tools #E0584E   Bio  #34C759   NerdStack #7C3AED (family violet)
 */

const SUITE_MASTER = {
  chem: "#F8B84E",
  math: "#2DD4BF",
  phys: "#22B8F0",
  tools: "#E0584E",
  bio: "#34C759",
  stack: "#7C3AED",
  games: "#EC4899",
  music: "#D946EF",
  history: "#C9A227",
};

// Per-tool START hue, keyed by exact tool name. Chosen to be distinct
// within a suite and clamped away from near-white so the light end
// stays >= ~contrast floor on #0A0B14.
const TOOL_START = {
  // NerdChem → resolve to amber
  "Periodic Table": "#5BB8A8",
  "Molecular Weight": "#D9645B",
  "Mole Calculator": "#C77BD4",
  "pH / pOH Calculator": "#5BA8D9",
  "Dilution Calculator": "#6FB85B",
  "Significant Figures": "#D99A4E",
  "Equation Balancer": "#9A8AD9",
  "Gas Law Solver": "#5B9AD9",
  "Stoichiometry": "#D97B9A",

  // NerdMath → resolve to teal
  "Scientific Calculator": "#8A7BD9",
  "Quadratic Solver": "#9A7BD9",
  "Trig & Unit Circle": "#D9994E",
  "Logarithm": "#8A7BD9",
  "Matrix": "#7B6FD9",

  // NerdPhys → resolve to cyan
  "Projectile Motion": "#5BD9C8",
  "Kinematics (SUVAT)": "#7BB8D9",
  "Newton's 2nd Law": "#9A8AD9",
  "Energy & Work": "#D9C84E",
  "Ohm's Law": "#5BD99A",

  // NerdTools → resolve to darkened-red
  "Unit Converter": "#D9994E",
  "Bubble Level": "#5BB8A8",
  "Compass": "#5B9AD9",
  "Decibel Meter": "#C77BD4",
  "Screen Ruler": "#6FB85B",
  "QR Code": "#9A8AD9",

  // NerdBio → resolve to green
  "DNA / RNA Toolkit": "#5BB8D9",
  "Codon Translator": "#D9C84E",
  "Punnett Square": "#5BA8C8",
  "Hardy-Weinberg": "#9AD95B",
  "Lab Solutions": "#5BD9B8",
  "Anatomy Reference": "#D9945B",

  // NerdStack root tools → resolve to family violet
  "GradeCalc": "#3FB8A8",
  "StudyTimer": "#D95B9A",
  "WordCount": "#5B6FD9",
  "PercentCalc": "#3F9AD9",
  "UnitConvert": "#6FB83F",
  "AverageCalc": "#D9853F",
  "QuizCard": "#9A6BFF",
  "ClearLens": "#A06BD9",
  "Suggest a tool": "#9A6BFF",
  "Particle Reference": "#5BD9C8",
  "Wave Calculator": "#5BB8E0",
  "Momentum & Collisions": "#7B9FD9",

  // V57.4 — Music & History Reference start hues. Pattern follows
  // other reference glyphs (Anatomy: warm orange → bio green, 108°
  // hue distance; Particle: cyan → phys cyan, 24° distance). For Music
  // a warm orange → magenta (~95° distance). For History an indigo →
  // brass (~150° distance, strong gradient).
  "Music Reference": "#D9945B",    // warm orange → music magenta #D946EF
  "History Reference": "#7B6FD9",  // indigo → history brass #C9A227

  // V57.4 — Book-glyph references that were missing TOOL_START entries
  // and therefore rendered as solid suite-master with no gradient. Same
  // warm-orange-start pattern as Anatomy Reference for the suites whose
  // master is cool; for Chemistry (already warm amber), we use a cool
  // sky-blue start to create the same orange→cool transition in reverse.
  "Biology Reference":   "#D9945B",  // orange → bio green #34C759 (~108°)
  "Math Reference":      "#D9945B",  // orange → math teal #2DD4BF (~140°)
  "Physics Reference":   "#D9945B",  // orange → phys cyan #22B8F0 (~165°)
  "Chemistry Reference": "#5BB8D9",  // sky blue → chem amber #F8B84E (~165°)
  // NerdTools EXPANSION (V51) — 36 new tools.
  // Family 1: Level & Measure → teal-green region (matches Bubble Level's #5BB8A8)
  "Level Pro": "#5BB8A8",         // direct replacement for Bubble Level, same hue
  "Picture-Hanging Guide": "#6FB89A",
  "Object Height": "#4FB89F",
  "Symmetry Checker": "#5BB890",
  "Photo Straightener": "#7BB89F",

  // Family 2: Visual & Camera → warm-orange / amber region
  "Magnifier": "#D9994E",
  "Color Picker": "#D97B5B",
  "Brightness Meter": "#D9B84E",
  "Grid Overlay Camera": "#D9A55B",
  "Before / After": "#D98B5B",

  // Family 3: Sound & Audio → violet / pink region (matches Decibel's #C77BD4)
  "Tone Generator": "#A87BD4",
  "Tuning Fork": "#B45BD4",
  "Metronome": "#C77BB4",
  "Spectrogram": "#9A6BD4",

  // Family 4: Outdoor & Navigation → blue region (matches Compass's #5B9AD9)
  "GPS Coordinates": "#5B9AD9",
  "Altimeter": "#5BB8D9",
  "Sun & Moon": "#7B9AD9",
  "Trail Marker": "#5B8AD9",

  // Family 5: DIY & Workshop → red-orange region (closest to suite master)
  "Metal Detector": "#D9654E",
  "Square Checker": "#D97B4E",
  "Drill Bit Guide": "#C9654E",
  "Roof Pitch": "#D9554E",
  "Coverage Calculator": "#E07B5B",

  // Family 6: Math & Engineering → yellow-amber region
  "Fraction Calculator": "#D9C84E",
  "Right Triangle": "#D9B85B",
  "Resistor Color Code": "#D9A84E",
  "Bolt Torque": "#C9A55B",

  // Family 7: Time & Counting → pink / rose region
  "Interval Timer": "#D97B9A",
  "Tally Counter": "#D96B8A",
  "Reaction Timer": "#E08B9A",

  // Family 8: Sensor Lab → green region (matches Ruler's #6FB85B, distinct from F1)
  "Vibration Meter": "#6FB85B",
  "Magnetic Field": "#5BB86F",
  "Hand Steadiness": "#7BB85B",
  "Shake / Rotation": "#5BB855",

  // Family 9: Practical / Generators → mixed (varied — these are diverse tools)
  "Currency Converter": "#9A8AD9",  // matches QR Code's hue
  "Password Generator": "#A87BD4",
  "EXIF Remover": "#7BB89A",
};

const SUITE_BY_PREFIX = (to = "") => {
  if (to.startsWith("/chem")) return "chem";
  if (to.startsWith("/math")) return "math";
  if (to.startsWith("/phys")) return "phys";
  if (to.startsWith("/tools")) return "tools";
  if (to.startsWith("/bio")) return "bio";
  if (to.startsWith("/music")) return "music";     // V57.4: was falling through to "stack"
  if (to.startsWith("/history")) return "history"; // V57.4: was falling through to "stack"
  return "stack"; // root NerdStack tools
};

// V45.1 — detect if a path is a game URL under a family subfolder.
// /games/chem/<gameid>, /games/math/<gameid>, /games/bio/<gameid>,
// /games/phys/<gameid>. Returns the family key, or null if not a game.
const GAME_FAMILY_BY_PATH = (to = "") => {
  const m = /^\/games\/(chem|math|bio|phys)\//.exec(to);
  return m ? m[1] : null;
};

/**
 * Returns { from, to, id } for a tool: the gradient start hue, the
 * suite master end hue, and a stable gradient id. If a tool has no
 * explicit start hue it falls back to the suite master at both ends
 * (a clean solid — still on-system, never broken).
 *
 * V45.1 — game tiles (paths under /games/<family>/) use the PARENT
 * gradient rule (family-color → violet) instead of the tool rule.
 * This matches the locked SuiteMark pattern shown on the /games hub:
 * each family icon resolves from its own hue → family violet.
 */
export function gradientFor(name, toPath) {
  // Game-tile path: use parent gradient (family → violet).
  const gameFamily = GAME_FAMILY_BY_PATH(toPath);
  if (gameFamily) {
    const g = PARENT_GRAD[gameFamily];
    const id = "tg-" + (name || "x").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
    return { from: g.from, to: g.to, id };
  }
  // Standard tool path: tool start hue → suite master.
  const suite = SUITE_BY_PREFIX(toPath);
  const end = SUITE_MASTER[suite];
  const start = TOOL_START[name] || end;
  const id = "tg-" + (name || "x").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  return { from: start, to: end, id };
}

export { SUITE_MASTER };

/**
 * Darken a #rrggbb hex toward black by `amount` (0–1). Deterministic,
 * no deps. Used for the PARENT suite-mark gradient rule.
 */
export function deepenHue(hex, amount = 0.42) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const r = Math.round(((n >> 16) & 255) * (1 - amount));
  const g = Math.round(((n >> 8) & 255) * (1 - amount));
  const b = Math.round((n & 255) * (1 - amount));
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

/**
 * PARENT suite-mark gradient (distinct from the tool rule above):
 * runs the suite's own hue → a deepened shade of the SAME hue. A
 * parent is its own family anchor; it does NOT resolve toward the
 * family violet the way tools resolve toward their suite master.
 * Returns { from, to, id }.
 */
// Parent SuiteMark gradient — EXACT values recovered from nerdstack-V32
// (the version Ian approved). Each suite's hue → family violet #7C3AED,
// EXCEPT NerdStack which is lavender-violet → deep violet (Ian's own
// fix for "stack is the violet, can't gradient to itself"). Games
// follows the established rule (rose → family violet). This is the
// gradient-stroked-OUTLINE model (transparent tile), NOT a solid tile.
const PARENT_GRAD = {
  stack: { from: "#A78BFA", to: "#5B21B6" },
  chem:  { from: "#F8B84E", to: "#7C3AED" },
  math:  { from: "#2DD4BF", to: "#7C3AED" },
  phys:  { from: "#22B8F0", to: "#7C3AED" },
  tools: { from: "#E0584E", to: "#7C3AED" },
  bio:   { from: "#34C759", to: "#7C3AED" },
  games: { from: "#EC4899", to: "#7C3AED" },
  music: { from: "#D946EF", to: "#7C3AED" },
  history: { from: "#C9A227", to: "#7C3AED" },
};

export function parentGradient(suiteKey) {
  const g = PARENT_GRAD[suiteKey] || PARENT_GRAD.stack;
  return { from: g.from, to: g.to, id: "pg-" + suiteKey };
}
